import React from "react";
import { SEO } from "./SEO";
import { BlogArticle } from "../../types-var/blog";

// Helper function to treat null/undefined/empty string as missing
const safeValue = (value: any): any => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  return value;
};

interface BlogSEOProps {
  article: BlogArticle;
}

export const BlogSEO: React.FC<BlogSEOProps> = ({ article }) => {
  // Prefer backend SEO data if available
  const backendSEO = (article as any).seo || {};

  // Use backend SEO schema if available, otherwise fallback
  const articleSchema = safeValue(backendSEO.structuredData) || {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.description,
    image: article.cover?.formats?.thumbnail?.url || article.cover?.url,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
    author: article.blog_author
      ? {
          "@type": "Person",
          name: article.blog_author.name,
          image:
            article.blog_author.avatar?.formats?.thumbnail?.url ||
            article.blog_author.avatar?.url,
        }
      : undefined,
    publisher: {
      "@type": "Organization",
      name: "Mega Osaka",
      logo: {
        "@type": "ImageObject",
        url:
          typeof window !== "undefined"
            ? `${window.location.origin}/logo192.png`
            : "/logo192.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": typeof window !== "undefined" ? window.location.href : "",
    },
  };

  // Prefer backend keywords if available
  const keywords =
    safeValue(backendSEO.keywords) ||
    [
      article.title,
      ...(article.blog_categories?.map((cat) => cat.name) || []),
    ].join(", ");

  // Prefer backend image if available
  const image =
    safeValue(backendSEO.image) ||
    article.cover?.formats?.thumbnail?.url ||
    article.cover?.url;

  // Prefer backend author if available
  const author =
    safeValue(backendSEO.author) || article.blog_author?.name || "Mega Osaka";

  // Prefer backend publisher if available
  const publisher = safeValue(backendSEO.publisher) || "Mega Osaka";

  // Prefer backend description if available
  const description = safeValue(backendSEO.description) || article.description;

  // Prefer backend title if available
  const title = safeValue(backendSEO.title) || article.title;

  // Prefer backend openGraph if available
  const openGraph = safeValue(backendSEO.openGraph) || undefined;

  return (
    <SEO
      title={title}
      description={description}
      image={image}
      type="article"
      schema={articleSchema}
      openGraph={openGraph}
      seoData={backendSEO}
      author={author}
      publisher={publisher}
      useGlobalFallbacks={true} // Enable global SEO fallbacks
    >
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta property="article:published_time" content={article.publishedAt} />
      <meta property="article:modified_time" content={article.updatedAt} />
      {article.blog_categories?.map((category, index) => (
        <meta key={index} property="article:tag" content={category.name} />
      ))}
      <meta
        property="article:section"
        content={article.blog_categories?.[0]?.name}
      />
      <meta property="article:author" content={author} />

      {/* Open Graph article specific tags */}
      <meta
        property="og:article:published_time"
        content={article.publishedAt}
      />
      <meta property="og:article:modified_time" content={article.updatedAt} />
      <meta property="og:article:author" content={author} />
      {article.blog_categories?.map((category, index) => (
        <meta
          key={`og-${index}`}
          property="og:article:tag"
          content={category.name}
        />
      ))}
      <meta
        property="og:article:section"
        content={article.blog_categories?.[0]?.name}
      />
    </SEO>
  );
};

export default BlogSEO;
